// frontend/src/components/tiles/ProductOnlyTile.jsx
import React from 'react';
import { Stack, Typography, Box, Chip, Tooltip, LinearProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TileShell from './TileShell';
import InterviewsBadge from './InterviewsBadge';
import ReceiptsList from './ReceiptsList';
import { describeSatisfactionLevel } from '../../utils/qualitative';


/* ---------- helpers ---------- */

const toNum = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
};

const avg = (arr = []) => {
  const nums = arr.map(toNum).filter((n) => n != null);
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

/**
 * Aggregate positives/drawbacks across interviews and compute a 0–10 score:
 *   score10 = 0.7 * (interviewShare * 10) + 0.3 * ((avgImportance/5) * 10)
 * Returns rows sorted by score10 desc.
 */
function aggregateAndScoreCategories({
  mentions = [],
  arrayKey,
  categoryKey,
  importanceKey,
  weightCount = 0.7,
  weightImportance = 0.3,
}) {
  const byCat = new Map(); // name -> { interviewSet:Set(idx), importances:number[] }

  mentions.forEach((m, idx) => {
    const arr = Array.isArray(m[arrayKey]) ? m[arrayKey] : [];
    const seenThis = new Set();
    for (const it of arr) {
      const name = it?.[categoryKey];
      const imp = toNum(it?.[importanceKey]);
      if (!name || imp == null) continue;
      if (!byCat.has(name)) byCat.set(name, { interviewSet: new Set(), importances: [] });
      if (!seenThis.has(name)) {
        byCat.get(name).interviewSet.add(idx);
        seenThis.add(name);
      }
      byCat.get(name).importances.push(imp);
    }
  });

  const totalInterviews = Math.max(mentions.length, 1);

  const rows = [...byCat.entries()].map(([name, data]) => {
    const countInterviews = data.interviewSet.size;
    const share = countInterviews / totalInterviews;     // 0..1
    const avgImp = data.importances.length
      ? data.importances.reduce((s, v) => s + v, 0) / data.importances.length
      : 0; // 1..5 typical
    const score10 =
      (weightCount * (share * 10)) +
      (weightImportance * ((avgImp / 5)));
    const pct = Math.round(share * 100);
    return { name, countInterviews, pct, avgImportance: avgImp, score10 };
  });

  rows.sort((a, b) => b.score10 - a.score10);
  return rows;
}

/* ---------- UI pieces ---------- */

// Satisfaction pill (previous style): "Satisfaction: {num} · {label}" + bar
function SatisfactionPill({ value1to5 }) {
  const v = toNum(value1to5);
  const show = v != null;
  const val10 = show ? v * 2 : 0; // map 1..5 → 2..10 for label/bar on 0..10 scale
  const label = show ? describeSatisfactionLevel(val10) : '';
  const display = show ? v.toFixed(1) : '—';
  const pct = Math.max(0, Math.min(100, (val10 / 10) * 100));

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        px: 1,
        py: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Satisfaction: {display}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label || ''}
      </Typography>
      <Box sx={{ width: 90 }}>
        <LinearProgress variant="determinate" value={pct} />
      </Box>
    </Box>
  );
}

// Green/Red score pill with prefix text and tooltip
function ScorePill({ color = 'success', kind = 'Positivity', score10 = 0, tooltip = '' }) {
  // keep one decimal for display
  const disp = Number.isFinite(score10) ? score10.toFixed(1) : '—';
  const label = `${kind} score: ${disp}`;
  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        color={color}
        variant="outlined"
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{label}</span>
            <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.85 }} />
          </Box>
        }
      />
    </Tooltip>
  );
}

/* ---------- component ---------- */

/**
 * ProductOnlyTile
 * props:
 *  - title: string (solution_choice_category)
 *  - mentions: array (one entry per interview mentioning this product)
 *      each mention may include:
 *        satisfaction_level: number|string
 *        positives: [{ positive_category, positive_importance, positive_importance_quote }]
 *        drawbacks: [{ drawback_category, drawback_importance, drawback_importance_quote }]
 *  - sectionTotal: number
 *  - receipts: string[]
 */
export default function ProductOnlyTile({ title, mentions = [], sectionTotal = 0, receipts = [] }) {
  // Interviews badge
  const interviewCount = mentions.length;
  const percent = sectionTotal ? Math.round((interviewCount / sectionTotal) * 100) : 0;

  // Satisfaction: average to 1 decimal; label from scoring.describe on 0..10 scale
  const satAvg1to5 = avg(mentions.map((m) => m?.satisfaction_level));

  // Aggregate positives & drawbacks with 0–10 score (70% share, 30% importance)
  const positives = aggregateAndScoreCategories({
    mentions,
    arrayKey: 'positives',
    categoryKey: 'positive_category',
    importanceKey: 'positive_importance',
    weightCount: 0.7,
    weightImportance: 0.3,
  }).slice(0, 2);

  const drawbacks = aggregateAndScoreCategories({
    mentions,
    arrayKey: 'drawbacks',
    categoryKey: 'drawback_category',
    importanceKey: 'drawback_importance',
    weightCount: 0.7,
    weightImportance: 0.3,
  }).slice(0, 2);

  const GoodBadList = ({ title, rows, color, kind }) => (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {rows.length ? (
        <Stack spacing={0.5}>
          {rows.map((r) => {
            const tip = `${kind} score = 0.7 × (Interview share ${r.pct}%) + 0.3 × (Avg importance ${(r.avgImportance ?? 0).toFixed(1)} / 5), scaled to 0–10`;
            return (
              <Box key={r.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {r.name}
                </Typography>
                <ScorePill
                  color={color}
                  kind={kind}
                  score10={r.score10}
                  tooltip={tip}
                />
                {/* per request: hide counts/avg text after the pill */}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          None observed
        </Typography>
      )}
    </Box>
  );

  const metricsRow = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      <InterviewsBadge percent={percent} count={interviewCount} />
      <SatisfactionPill value1to5={satAvg1to5} />
    </Box>
  );

  const body = (
    <Stack spacing={1.25}>
      <GoodBadList title="The good" rows={positives} color="success" kind="Positivity" />
      <GoodBadList title="The bad" rows={drawbacks} color="error" kind="Negativity" />
    </Stack>
  );

  return (
    <TileShell
      typePill="product"
      title={title}
      metricsRow={metricsRow}
      body={body}
      receipts={receipts?.length ? <ReceiptsList receipts={receipts} /> : null}
    />
  );
}
